import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { Rol } from '@/types/enums';
import { CANAL_ENVIO_LABELS, TIPO_PROFESIONAL_LABELS } from '@/utils/labels';

import type { Profesional, ProfesionalListParams } from '../types';

type SortBy = NonNullable<ProfesionalListParams['sort_by']>;
type SortOrder = NonNullable<ProfesionalListParams['sort_order']>;

interface ProfesionalesTableProps {
  items: Profesional[];
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSortChange: (sortBy: SortBy, sortOrder: SortOrder) => void;
  onDelete: (profesional: Profesional) => void;
}

export function ProfesionalesTable({
  items,
  sortBy,
  sortOrder,
  onSortChange,
  onDelete,
}: ProfesionalesTableProps): JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canDelete = currentUser?.rol === Rol.ADMINISTRADOR;

  function handleSortClick(column: SortBy): void {
    if (sortBy === column) {
      onSortChange(column, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(column, 'asc');
    }
  }

  function handleRowClick(uid: string): void {
    navigate(`/profesionales/${uid}`);
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' } }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={sortBy === 'nombre_apellido' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'nombre_apellido'}
                direction={sortBy === 'nombre_apellido' ? sortOrder : 'asc'}
                onClick={() => handleSortClick('nombre_apellido')}
              >
                Nombre y apellido
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'tipo_profesional' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'tipo_profesional'}
                direction={sortBy === 'tipo_profesional' ? sortOrder : 'asc'}
                onClick={() => handleSortClick('tipo_profesional')}
              >
                Tipo
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'localidad' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'localidad'}
                direction={sortBy === 'localidad' ? sortOrder : 'asc'}
                onClick={() => handleSortClick('localidad')}
              >
                Localidad
              </TableSortLabel>
            </TableCell>
            <TableCell>Canal</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              Acciones
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((p) => (
            <TableRow
              key={p.uid_profesional}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => handleRowClick(p.uid_profesional)}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {p.nombre_apellido}
                </Typography>
                {p.emails.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {p.emails[0]}
                    {p.emails.length > 1 && ` (+${p.emails.length - 1})`}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={
                    p.tipo_profesional === 'otro' && p.tipo_profesional_otro
                      ? p.tipo_profesional_otro
                      : TIPO_PROFESIONAL_LABELS[p.tipo_profesional]
                  }
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{p.localidad ?? '—'}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{CANAL_ENVIO_LABELS[p.envio_informe]}</Typography>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="Editar">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(p.uid_profesional);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(p);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
